/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { MdArrowBack, MdAutoAwesome, MdExpandLess, MdExpandMore, MdInsights, MdOutlineQuiz, MdUploadFile } from "react-icons/md";

const API = process.env.REACT_APP_API_URL;

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("de-DE");
}

function formatPeriod(start, end) {
  if (!start || !end) return "-";
  return `${formatDate(start)} bis ${formatDate(end)}`;
}

function normalizeRoutine(series) {
  return {
    ...series,
    occurrences: Array.isArray(series?.rounds) ? series.rounds : [],
  };
}

function OccurrenceBadge({ status }) {
  const map = {
    current: { label: "Aktuelle Woche", className: "bg-[#DDF0FF] text-[#265DAB]" },
    next: { label: "Nächste Woche", className: "bg-[#FFF3D9] text-[#8A5A00]" },
    past: { label: "Vergangen", className: "bg-gray-200 text-gray-700" },
    future: { label: "Zukünftig", className: "bg-gray-100 text-gray-700" },
  };

  const item = map[status] || map.future;

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${item.className}`}>
      {item.label}
    </span>
  );
}

function OccurrenceCard({ occurrence, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(occurrence.id)}
      className={[
        "w-full rounded-2xl border p-5 text-left shadow-sm transition",
        selected
          ? "border-[#76B3FF] bg-[#EFF7FF]"
          : "border-black/10 bg-white hover:border-[#76B3FF]/60 hover:bg-[#F8FBFF]",
      ].join(" ")}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{occurrence.title}</h3>
          <p className="mt-2 text-sm text-gray-600">
            {formatPeriod(occurrence.period_start, occurrence.period_end)}
          </p>
        </div>
        <OccurrenceBadge status={occurrence.status} />
      </div>

      <div className="mt-4 flex flex-col gap-2 text-sm text-gray-700">
        <div>{occurrence.question_count} Frage{occurrence.question_count === 1 ? "" : "n"}</div>
        {occurrence.status === "next" && occurrence.can_auto_generate_later && occurrence.question_count === 0 && (
          <div>Wird automatisch generiert, falls du nichts vorbereitest.</div>
        )}
        {occurrence.status === "past" && (
          <div>Nur Ansicht</div>
        )}
      </div>
    </button>
  );
}

function ReadOnlyQuestionCard({ question }) {
  return (
    <article className="rounded-xl border border-black/10 bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Frage {question.position}
          </div>
          <p className="mt-1 text-sm font-medium text-gray-900">{question.question_text}</p>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Antwort
          </div>
          <p className="mt-1 text-sm text-gray-700">
            {question.answer_text || "Keine Antwort hinterlegt."}
          </p>
        </div>
      </div>
    </article>
  );
}

function EditableQuestionCard({
  question,
  onSave,
  onDelete,
  saving = false,
  deleting = false,
}) {
  const [questionText, setQuestionText] = useState(question.question_text || "");
  const [answerText, setAnswerText] = useState(question.answer_text || "");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    setQuestionText(question.question_text || "");
    setAnswerText(question.answer_text || "");
    setLocalError("");
  }, [question.id, question.question_text, question.answer_text]);

  const dirty =
    questionText !== (question.question_text || "") ||
    answerText !== (question.answer_text || "");

  async function handleSave() {
    const trimmedQuestion = questionText.trim();
    if (!trimmedQuestion) {
      setLocalError("Die Frage darf nicht leer sein.");
      return;
    }

    setLocalError("");
    await onSave(question.id, {
      question_text: trimmedQuestion,
      answer_text: answerText.trim(),
    });
  }

  return (
    <article className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Frage {question.position}
          </div>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">Bearbeiten</h3>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || deleting || !dirty}
            className={[
              "rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition",
              saving || deleting || !dirty
                ? "cursor-not-allowed bg-gray-200 text-gray-500"
                : "bg-[#76B3FF] text-white hover:bg-[#5d9ef7]",
            ].join(" ")}
          >
            {saving ? "Speichert..." : "Speichern"}
          </button>
          <button
            type="button"
            onClick={() => onDelete(question.id)}
            disabled={saving || deleting}
            className={[
              "rounded-lg border px-4 py-2 text-sm transition",
              saving || deleting
                ? "cursor-not-allowed border-gray-200 text-gray-400"
                : "border-red-200 text-red-700 hover:bg-red-50",
            ].join(" ")}
          >
            {deleting ? "Löscht..." : "Löschen"}
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4">
        <label className="text-sm text-gray-700">
          Frage
          <textarea
            value={questionText}
            onChange={(event) => setQuestionText(event.target.value)}
            rows={3}
            className="mt-2 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm"
          />
        </label>

        <label className="text-sm text-gray-700">
          Antwort
          <textarea
            value={answerText}
            onChange={(event) => setAnswerText(event.target.value)}
            rows={2}
            className="mt-2 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm"
          />
        </label>
      </div>

      {localError && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {localError}
        </div>
      )}
    </article>
  );
}

export default function QuizzesPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [routines, setRoutines] = useState([]);
  const [selectedRoutineId, setSelectedRoutineId] = useState(null);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [pageError, setPageError] = useState("");
  const [notice, setNotice] = useState("");
  const [quizGeneration, setQuizGeneration] = useState(null);
  const [showPastOccurrences, setShowPastOccurrences] = useState(false);

  const [actionMode, setActionMode] = useState(null);
  const [topicHints, setTopicHints] = useState("");
  const [questionCount, setQuestionCount] = useState("");
  const [difficulty, setDifficulty] = useState("mittel");
  const [manualQuestion, setManualQuestion] = useState("");
  const [manualAnswer, setManualAnswer] = useState("");
  const [formError, setFormError] = useState("");

  const [savingQuestionId, setSavingQuestionId] = useState(null);
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  const [questionEditMode, setQuestionEditMode] = useState(false);

  const selectedRoutine = routines.find((routine) => routine.id === selectedRoutineId) || null;
  const selectedOccurrence = selectedRoutine?.occurrences?.find(
    (occurrence) => occurrence.id === selectedOccurrenceId
  ) || null;
  const hasExistingQuestions = (selectedOccurrence?.question_count || 0) > 0;

  const currentOccurrence = selectedRoutine?.occurrences?.find((occurrence) => occurrence.status === "current") || null;
  const nextOccurrence = selectedRoutine?.occurrences?.find((occurrence) => occurrence.status === "next") || null;
  const pastOccurrences = (selectedRoutine?.occurrences || []).filter((occurrence) => occurrence.status === "past");

  function chooseOccurrenceForRoutine(routine, preferredOccurrenceId = null) {
    if (!routine) return null;
    const occurrenceIds = (routine.occurrences || []).map((occurrence) => occurrence.id);

    if (preferredOccurrenceId && occurrenceIds.includes(preferredOccurrenceId)) {
      return preferredOccurrenceId;
    }

    const current = (routine.occurrences || []).find((occurrence) => occurrence.status === "current");
    if (current) return current.id;

    const next = (routine.occurrences || []).find((occurrence) => occurrence.status === "next");
    if (next) return next.id;

    return routine.occurrences?.[0]?.id || null;
  }

  async function loadRoutines(preferred = {}) {
    setLoading(true);
    setPageError("");

    try {
      const res = await fetch(`${API}/shared-quiz-series`, { credentials: "include" });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || payload.detail || "Quiz-Serien konnten nicht geladen werden.");
      if (!payload.quiz_generation) throw new Error("Quiz-Konfiguration konnte nicht geladen werden.");

      const nextRoutines = Array.isArray(payload.series) ? payload.series.map(normalizeRoutine) : [];
      setQuizGeneration(payload.quiz_generation);
      setRoutines(nextRoutines);

      if (nextRoutines.length === 0) {
        setSelectedRoutineId(null);
        setSelectedOccurrenceId(null);
        return;
      }

      const nextRoutineId =
        (preferred.routineId && nextRoutines.some((routine) => routine.id === preferred.routineId))
          ? preferred.routineId
          : (selectedRoutineId && nextRoutines.some((routine) => routine.id === selectedRoutineId))
            ? selectedRoutineId
            : nextRoutines[0].id;

      const nextRoutine = nextRoutines.find((routine) => routine.id === nextRoutineId) || nextRoutines[0];
      const nextOccurrenceId = chooseOccurrenceForRoutine(
        nextRoutine,
        preferred.occurrenceId || selectedOccurrenceId
      );

      setSelectedRoutineId(nextRoutineId);
      setSelectedOccurrenceId(nextOccurrenceId);
    } catch (error) {
      setPageError(error?.message || "Quiz-Serien konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRoutines();
  }, []);

  useEffect(() => {
    setActionMode(null);
    setFormError("");
    setQuestionEditMode(false);
  }, [selectedOccurrenceId]);

  useEffect(() => {
    setShowPastOccurrences(false);
  }, [selectedRoutineId]);

  function openFilePicker() {
    if (!selectedOccurrence || !selectedOccurrence.is_editable || actionBusy) return;
    fileInputRef.current?.click();
  }

  function togglePastOccurrences() {
    setShowPastOccurrences((current) => {
      const nextShow = !current;
      if (!nextShow && selectedOccurrence?.status === "past") {
        setSelectedOccurrenceId(currentOccurrence?.id || nextOccurrence?.id || null);
      }
      return nextShow;
    });
  }

  async function handleCreateRoutine(event) {
    event.preventDefault();
    setPageError("");
    setNotice("");
    setFormError("");

    setActionBusy(true);

    try {
      const res = await fetch(`${API}/shared-quiz-series`, {
        method: "POST",
        credentials: "include",
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || payload.detail || "Quiz-Serie konnte nicht erstellt werden.");

      const routine = normalizeRoutine(payload.series);
      const current = (routine.occurrences || []).find((occurrence) => occurrence.status === "current");

      await loadRoutines({
        routineId: routine.id,
        occurrenceId: current?.id || null,
      });
      setNotice("Quiz-Serie erstellt. Die aktuelle Woche wurde vorbereitet.");
    } catch (error) {
      setPageError(error?.message || "Quiz-Serie konnte nicht erstellt werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleGenerateQuestions(event) {
    event.preventDefault();
    if (!selectedRoutine || !selectedOccurrence || !quizGeneration) return;
    const appendQuestions = selectedOccurrence.question_count > 0;

    setPageError("");
    setNotice("");
    setFormError("");

    const parsedCount = Number(questionCount);
    if (
      !Number.isInteger(parsedCount) ||
      parsedCount < quizGeneration.min_question_count ||
      parsedCount > quizGeneration.max_question_count
    ) {
      setFormError(
        `Die Anzahl der Fragen muss zwischen ${quizGeneration.min_question_count} und ${quizGeneration.max_question_count} liegen.`
      );
      return;
    }

    setActionBusy(true);

    try {
      const res = await fetch(
        `${API}/shared-quiz-series/${selectedRoutine.id}/rounds/${selectedOccurrence.id}/generate`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            topic_hints: topicHints.trim(),
            question_count: parsedCount,
            difficulty,
          }),
        }
      );
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || payload.detail || "Fragen konnten nicht generiert werden.");

      await loadRoutines({
        routineId: selectedRoutine.id,
        occurrenceId: selectedOccurrence.id,
      });
      setActionMode(null);
      setNotice(appendQuestions ? "Die neuen Fragen wurden hinzugefügt." : "Die Fragen wurden generiert.");
    } catch (error) {
      setPageError(error?.message || "Fragen konnten nicht generiert werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleManualQuestionSubmit(event) {
    event.preventDefault();
    if (!selectedRoutine || !selectedOccurrence) return;

    setPageError("");
    setNotice("");
    setFormError("");

    if (!manualQuestion.trim()) {
      setFormError("Bitte gib eine Frage ein.");
      return;
    }

    setActionBusy(true);

    try {
      const res = await fetch(
        `${API}/shared-quiz-series/${selectedRoutine.id}/rounds/${selectedOccurrence.id}/questions`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question_text: manualQuestion.trim(),
            answer_text: manualAnswer.trim(),
          }),
        }
      );
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || payload.detail || "Die Frage konnte nicht hinzugefügt werden.");

      setManualQuestion("");
      setManualAnswer("");
      setActionMode(null);
      await loadRoutines({
        routineId: selectedRoutine.id,
        occurrenceId: selectedOccurrence.id,
      });
      setNotice("Die Frage wurde hinzugefügt.");
    } catch (error) {
      setPageError(error?.message || "Die Frage konnte nicht hinzugefügt werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file || !selectedRoutine || !selectedOccurrence) return;

    const formData = new FormData();
    formData.append("file", file);

    setActionBusy(true);
    setPageError("");
    setNotice("");

    try {
      const res = await fetch(
        `${API}/shared-quiz-series/${selectedRoutine.id}/rounds/${selectedOccurrence.id}/import-from-file`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || payload.detail || "Die Datei konnte nicht verarbeitet werden.");

      await loadRoutines({
        routineId: selectedRoutine.id,
        occurrenceId: selectedOccurrence.id,
      });
      setNotice("Die Fragen wurden aus dem Dokument übernommen.");
      setActionMode(null);
    } catch (error) {
      setPageError(error?.message || "Die Datei konnte nicht verarbeitet werden.");
    } finally {
      setActionBusy(false);
      event.target.value = "";
    }
  }

  async function handleDeleteAllQuestions() {
    if (!selectedRoutine || !selectedOccurrence) return;
    if (!window.confirm("Möchtest du wirklich alle Fragen löschen?")) return;

    setActionBusy(true);
    setPageError("");
    setNotice("");

    try {
      const res = await fetch(
        `${API}/shared-quiz-series/${selectedRoutine.id}/rounds/${selectedOccurrence.id}/questions`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || payload.detail || "Die Fragen konnten nicht gelöscht werden.");

      await loadRoutines({
        routineId: selectedRoutine.id,
        occurrenceId: selectedOccurrence.id,
      });
      setNotice("Alle Fragen wurden gelöscht.");
    } catch (error) {
      setPageError(error?.message || "Die Fragen konnten nicht gelöscht werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleSaveQuestion(questionId, nextValues) {
    setSavingQuestionId(questionId);
    setPageError("");
    setNotice("");

    try {
      const res = await fetch(`${API}/shared-quiz-questions/${questionId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nextValues),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || payload.detail || "Die Frage konnte nicht gespeichert werden.");

      await loadRoutines({
        routineId: selectedRoutine?.id,
        occurrenceId: selectedOccurrence?.id,
      });
      setNotice("Die Frage wurde aktualisiert.");
    } catch (error) {
      setPageError(error?.message || "Die Frage konnte nicht gespeichert werden.");
    } finally {
      setSavingQuestionId(null);
    }
  }

  async function handleDeleteQuestion(questionId) {
    setDeletingQuestionId(questionId);
    setPageError("");
    setNotice("");

    try {
      const res = await fetch(`${API}/shared-quiz-questions/${questionId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || payload.detail || "Die Frage konnte nicht gelöscht werden.");

      await loadRoutines({
        routineId: selectedRoutine?.id,
        occurrenceId: selectedOccurrence?.id,
      });
      setNotice("Die Frage wurde gelöscht.");
    } catch (error) {
      setPageError(error?.message || "Die Frage konnte nicht gelöscht werden.");
    } finally {
      setDeletingQuestionId(null);
    }
  }

  return (
    <div className="flex min-h-screen bg-[#cdeaff]">
      <Sidebar active="cognitive-exercises" />

      <main className="flex-1 px-0 pt-6 pb-0">
        <div className="h-full w-full rounded-tl-xl border border-black/10 bg-white/60 p-6 backdrop-blur">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            onChange={handleFileChange}
          />

          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <button
                type="button"
                onClick={() => navigate("/cognitive-uebungen")}
                className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:bg-[#F8FBFF]"
              >
                <MdArrowBack className="text-base" />
                Zurück zu Kognitive Übungen
              </button>

              <h1 className="mt-4 text-3xl font-semibold text-gray-900">Quizzes</h1>

            </div>

            <button
              type="button"
              onClick={() => navigate("/quizzes/analysis")}
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-[#F8FBFF]"
            >
              <MdInsights className="text-base" />
              Analysis
            </button>
          </div>

          {loading && (
            <div className="mt-6 text-sm text-gray-700">Lade Quiz-Routinen...</div>
          )}

          {pageError && (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {pageError}
            </div>
          )}

          {notice && (
            <div className="mt-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              {notice}
            </div>
          )}

          {!loading && routines.length === 0 && (
            <section className="mt-8 rounded-3xl border border-dashed border-black/15 bg-white/70 p-8">
              <div className="max-w-3xl">
                <h2 className="text-2xl font-semibold text-gray-900">Erste Quiz-Routine einrichten</h2>
                <p className="mt-3 text-sm leading-6 text-gray-700">
                  Lege zuerst eine wöchentliche Quiz-Routine an. Direkt danach wirst du
                  zur Einrichtung des Quiz für diese aktuelle Woche geführt.
                </p>
              </div>

              <form onSubmit={handleCreateRoutine} className="mt-6">
                {formError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {formError}
                  </div>
                )}

                <div className={formError ? "mt-4" : ""}>
                  <button
                    type="submit"
                    disabled={actionBusy}
                    className={[
                      "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition",
                      actionBusy
                        ? "cursor-not-allowed bg-gray-200 text-gray-500"
                        : "bg-[#76B3FF] text-white hover:bg-[#5d9ef7]",
                    ].join(" ")}
                  >
                    <MdOutlineQuiz className="text-base" />
                    {actionBusy ? "Erstellt..." : "Wöchentliche Routine erstellen"}
                  </button>
                </div>
              </form>
            </section>
          )}

          {!loading && routines.length > 0 && (
            <>
              {selectedRoutine && (
                <section className="mt-8 rounded-3xl border border-black/10 bg-white/70 p-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Wöchentliche Routine</h2>
                    <p className="mt-2 text-sm text-gray-600">
                      Diese Routine erstellt fortlaufend neue Quizwochen.
                    </p>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {currentOccurrence && (
                      <OccurrenceCard
                        occurrence={currentOccurrence}
                        selected={currentOccurrence.id === selectedOccurrenceId}
                        onSelect={setSelectedOccurrenceId}
                      />
                    )}
                    {nextOccurrence && (
                      <OccurrenceCard
                        occurrence={nextOccurrence}
                        selected={nextOccurrence.id === selectedOccurrenceId}
                        onSelect={setSelectedOccurrenceId}
                      />
                    )}
                  </div>

                  {pastOccurrences.length > 0 && (
                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={togglePastOccurrences}
                        className="flex w-full items-center justify-between rounded-2xl border border-black/10 bg-[#F8FBFF] px-5 py-4 text-left transition hover:border-[#76B3FF]/60 hover:bg-white"
                      >
                        <h3 className="text-lg font-semibold text-gray-900">
                          {showPastOccurrences ? "Vergangene Wochen ausblenden" : "Vergangene Wochen anzeigen"}
                        </h3>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-[#265DAB]">
                          {showPastOccurrences ? "Ausblenden" : "Anzeigen"}
                          {showPastOccurrences ? <MdExpandLess className="text-lg" /> : <MdExpandMore className="text-lg" />}
                        </span>
                      </button>

                      {showPastOccurrences && (
                        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
                          {pastOccurrences.map((occurrence) => (
                            <OccurrenceCard
                              key={occurrence.id}
                              occurrence={occurrence}
                              selected={occurrence.id === selectedOccurrenceId}
                              onSelect={setSelectedOccurrenceId}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </section>
              )}

              {selectedOccurrence && (
                <section className="mt-8 rounded-3xl border border-black/10 bg-white/70 p-6">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-2xl font-semibold text-gray-900">{selectedOccurrence.title}</h2>
                        <OccurrenceBadge status={selectedOccurrence.status} />
                      </div>
                      <p className="mt-2 text-sm text-gray-600">
                        {formatPeriod(selectedOccurrence.period_start, selectedOccurrence.period_end)}
                      </p>
                    </div>
                    <div className="rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm">
                      {selectedOccurrence.question_count} Frage{selectedOccurrence.question_count === 1 ? "" : "n"}
                    </div>
                  </div>

                  {selectedOccurrence.status === "past" && (
                    <p className="mt-4 text-sm text-gray-700">
                      Diese Woche ist abgeschlossen. Du kannst sie ansehen, aber nicht mehr bearbeiten.
                    </p>
                  )}

                  {selectedOccurrence.is_editable && (
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          if (!quizGeneration) return;
                          setActionMode("generate");
                          setQuestionCount(String(quizGeneration.default_question_count));
                          setFormError("");
                        }}
                        disabled={actionBusy || !quizGeneration}
                        className={[
                          "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition",
                          actionBusy || !quizGeneration
                            ? "cursor-not-allowed bg-gray-200 text-gray-500"
                            : "bg-[#76B3FF] text-white hover:bg-[#5d9ef7]",
                        ].join(" ")}
                      >
                        <MdAutoAwesome className="text-base" />
                        {hasExistingQuestions ? "Mehr Fragen generieren" : "Fragen generieren"}
                      </button>

                      <button
                        type="button"
                        onClick={openFilePicker}
                        disabled={actionBusy}
                        className={[
                          "inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition",
                          actionBusy
                            ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                            : "border-black/10 bg-white text-gray-700 hover:bg-[#F8FBFF]",
                        ].join(" ")}
                      >
                        <MdUploadFile className="text-base" />
                        Fragen aus Dokument laden
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setActionMode("manual");
                          setFormError("");
                        }}
                        disabled={actionBusy}
                        className="inline-flex items-center justify-center rounded-lg border border-black/10 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-[#F8FBFF]"
                      >
                        Frage manuell hinzufügen
                      </button>

                      {selectedOccurrence.question_count > 0 && (
                        <button
                          type="button"
                          onClick={() => setQuestionEditMode((current) => !current)}
                          className={[
                            "inline-flex items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition",
                            questionEditMode
                              ? "border-[#76B3FF] bg-[#EFF7FF] text-[#265DAB]"
                              : "border-black/10 bg-white text-gray-700 hover:bg-[#F8FBFF]",
                          ].join(" ")}
                        >
                          {questionEditMode ? "Kompakte Ansicht" : "Bearbeitungsmodus"}
                        </button>
                      )}

                      {selectedOccurrence.question_count > 0 && (
                        <button
                          type="button"
                          onClick={handleDeleteAllQuestions}
                          disabled={actionBusy}
                          className="inline-flex items-center justify-center rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50"
                        >
                          Alle Fragen löschen
                        </button>
                      )}
                    </div>
                  )}
                  

                  {actionMode === "generate" && selectedOccurrence.is_editable && (
                    <form
                      onSubmit={handleGenerateQuestions}
                      className="mt-6 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {hasExistingQuestions ? "Mehr Fragen generieren" : "Fragen generieren"}
                          </h3>
                          <p className="mt-1 text-sm text-gray-600">
                            {hasExistingQuestions
                              ? "Optional kannst du Themen vorgeben. Die neuen Fragen werden zu den vorhandenen Fragen hinzugefügt."
                              : "Optional kannst du Themen vorgeben. Ohne Themen wird die Routine selbst als Grundlage genutzt."}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setActionMode(null);
                            setFormError("");
                          }}
                          className="rounded-lg border border-black/10 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                        >
                          Schließen
                        </button>
                      </div>

                      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
                        <label className="text-sm text-gray-700">
                          Optionale Themen
                          <input
                            type="text"
                            value={topicHints}
                            onChange={(event) => setTopicHints(event.target.value)}
                            placeholder="z. B. Musik, Tiere, Geschichte"
                            className="mt-2 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm"
                          />
                        </label>

                        <label className="text-sm text-gray-700">
                          Fragenanzahl
                          <input
                            type="number"
                            min={quizGeneration?.min_question_count}
                            max={quizGeneration?.max_question_count}
                            value={questionCount}
                            onChange={(event) => setQuestionCount(event.target.value)}
                            className="mt-2 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm"
                          />
                        </label>

                        <label className="text-sm text-gray-700">
                          Schwierigkeit
                          <select
                            value={difficulty}
                            onChange={(event) => setDifficulty(event.target.value)}
                            className="mt-2 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm"
                          >
                            <option value="leicht">Leicht</option>
                            <option value="leicht bis mittel">Leicht bis mittel</option>
                            <option value="mittel">Mittel</option>
                            <option value="anspruchsvoll">Anspruchsvoll</option>
                            <option value="schwer">Schwer</option>
                          </select>
                        </label>
                      </div>

                      {formError && (
                        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                          {formError}
                        </div>
                      )}

                      <div className="mt-4">
                        <button
                          type="submit"
                          disabled={actionBusy}
                          className={[
                            "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition",
                            actionBusy
                              ? "cursor-not-allowed bg-gray-200 text-gray-500"
                              : "bg-[#76B3FF] text-white hover:bg-[#5d9ef7]",
                          ].join(" ")}
                        >
                          <MdAutoAwesome className="text-base" />
                          {actionBusy ? "Generiert..." : hasExistingQuestions ? "Jetzt hinzufügen" : "Jetzt generieren"}
                        </button>
                      </div>
                    </form>
                  )}

                  {actionMode === "manual" && selectedOccurrence.is_editable && (
                    <form
                      onSubmit={handleManualQuestionSubmit}
                      className="mt-6 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Frage manuell anlegen</h3>
                          <p className="mt-1 text-sm text-gray-600">
                            Du kannst einzelne Fragen jederzeit zur Woche hinzufügen.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setActionMode(null);
                            setFormError("");
                          }}
                          className="rounded-lg border border-black/10 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                        >
                          Schließen
                        </button>
                      </div>

                      <div className="mt-5 grid grid-cols-1 gap-4">
                        <label className="text-sm text-gray-700">
                          Frage
                          <textarea
                            value={manualQuestion}
                            onChange={(event) => setManualQuestion(event.target.value)}
                            rows={3}
                            className="mt-2 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm"
                          />
                        </label>

                        <label className="text-sm text-gray-700">
                          Antwort
                          <textarea
                            value={manualAnswer}
                            onChange={(event) => setManualAnswer(event.target.value)}
                            rows={2}
                            className="mt-2 w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm"
                          />
                        </label>
                      </div>

                      {formError && (
                        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                          {formError}
                        </div>
                      )}

                      <div className="mt-4">
                        <button
                          type="submit"
                          disabled={actionBusy}
                          className={[
                            "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition",
                            actionBusy
                              ? "cursor-not-allowed bg-gray-200 text-gray-500"
                              : "bg-[#76B3FF] text-white hover:bg-[#5d9ef7]",
                          ].join(" ")}
                        >
                          <MdOutlineQuiz className="text-base" />
                          {actionBusy ? "Speichert..." : "Frage hinzufügen"}
                        </button>
                      </div>
                    </form>
                  )}

                  {selectedOccurrence.question_count === 0 ? (
                    <div className="mt-6 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-gray-700">
                      Noch keine Fragen hinterlegt. Wenn du nichts machst, wird das Quiz zum Start dieser Woche automatisch generiert.
                    </div>
                  ) : (
                    <div className="mt-6 grid grid-cols-1 gap-4">
                      {(selectedOccurrence.questions || []).map((question) =>
                        selectedOccurrence.is_editable && questionEditMode ? (
                          <EditableQuestionCard
                            key={question.id}
                            question={question}
                            onSave={handleSaveQuestion}
                            onDelete={handleDeleteQuestion}
                            saving={savingQuestionId === question.id}
                            deleting={deletingQuestionId === question.id}
                          />
                        ) : (
                          <ReadOnlyQuestionCard
                            key={question.id}
                            question={question}
                          />
                        )
                      )}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
